// Решетката на работния лист „Прехвърляне": лепнат заглавен ред, плоски
// клетки (id `trx-{продукт}-{колона}`, стрелкова навигация от страницата),
// „тежката" клетка Партида е общият PickCell, ✕ на реда и празното състояние.
// Под десктоп ширина редовете са карти (TransferLineCards) със СЪЩИТЕ id-та на
// клетките. Логиката на редовете е в `useTransferRowsActions`.
import { Select } from "antd";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import { KbdChip } from "../../components/KbdChip";
import { PickCell } from "../../components/PickCell";
import { TruncatedText } from "../../components/list/TruncatedText";
import {
  SHEET_CONTROL_HEIGHT,
  SHEET_HEADER_CELL,
  SHEET_HEADER_ROW,
  SHEET_INPUT,
  SHEET_META_TEXT,
  SHEET_NAME_CELL,
  SHEET_NAME_TEXT,
  SHEET_ROW,
} from "../../components/list/density";
import { TransferLineCards } from "./TransferLineCards";
import type { TransferComputedRow } from "./useTransferRowsActions";
import {
  FLAT_CELL_CLASS,
  FLAT_CELL_ERROR_CLASS,
  LINE_GRID_CLASS,
  LINE_GRID_MIN_WIDTH_CLASS,
  cellId,
  lotLabel,
} from "./transferWorksheetShared";
import { TransferTotalsRow } from "./TransferTotalsRow";

export type TransferTableProps = {
  rows: TransferComputedRow[];
  fromWarehouseName: string | null;
  toWarehouseName: string | null;
  isDesktop: boolean;
  onQuantityChange: (productId: number, value: string) => void;
  onQuantityBlur: (productId: number) => void;
  onLotChange: (productId: number, lotId: number | null) => void;
  onRemove: (productId: number) => void;
  // ←/→ от отворения избор на партида → съседната клетка
  onSideExit: (productId: number, col: number, step: -1 | 1) => void;
  // сумите за реда „Общо" в дъното на решетката
  totalNet: number;
  totalGross: number;
};

const NUMBER_CLASS =
  "whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13px] leading-[normal]";

export const TransferWorksheetTable = (props: TransferTableProps) => {
  const {
    rows,
    fromWarehouseName,
    toWarehouseName,
    isDesktop,
    onQuantityChange,
    onQuantityBlur,
    onLotChange,
    onRemove,
    onSideExit,
    totalNet,
    totalGross,
  } = props;
  const { t } = useTranslation();

  if (rows.length === 0) return <TransferEmptyState isDesktop={isDesktop} />;
  // Под десктоп ширина — карти (същите id-та на клетките)
  if (!isDesktop) return <TransferLineCards {...props} />;

  return (
    <div className={`${LINE_GRID_MIN_WIDTH_CLASS} flex min-h-full flex-col`}>
      {/* Заглавен ред (sticky) */}
      <div
        className={`${LINE_GRID_CLASS} ${SHEET_HEADER_ROW} sticky top-0 z-[5] border-b border-shade-3 bg-fill`}
      >
        <Th center>#</Th>
        <Th>{t("inventoryPage.colProductName")}</Th>
        <Th>{t("inventoryPage.colUnitShort")}</Th>
        <Th right>{t("inventoryPage.colAvailable")}</Th>
        <Th right>{t("inventoryPage.colQty")}</Th>
        <Th>{t("inventoryPage.colBatch")}</Th>
        <Th right>{t("inventoryPage.trxRemains", { warehouse: fromWarehouseName ?? "" })}</Th>
        <Th right>{t("inventoryPage.trxBecomes", { warehouse: toWarehouseName ?? "" })}</Th>
        <Th right>{t("inventoryPage.colUnitPrice")}</Th>
        <Th right>{t("inventoryPage.colNet")}</Th>
        <Th right>{t("inventoryPage.colGross")}</Th>
        <div />
      </div>

      {rows.map((row, index) => {
        const unitSuffix = row.unit ? ` ${row.unit}` : "";
        return (
          <div
            key={row.productId}
            className={`${LINE_GRID_CLASS} ${SHEET_ROW} items-stretch border-b border-shade-2 ${
              row.overStock ? "bg-danger-tint" : row.quantity > 0 ? "bg-panel" : "bg-surface"
            }`}
          >
            <div className="flex items-center justify-center border-r border-shade-2 bg-fill font-[ui-monospace,Menlo,monospace] text-[11px] font-semibold leading-[normal] text-muted">
              {index + 1}
            </div>

            <div className={`${SHEET_NAME_CELL} border-r border-shade-2`}>
              <TruncatedText text={row.product?.name ?? `#${row.productId}`} className={SHEET_NAME_TEXT} />
              <span className={`${SHEET_META_TEXT} text-muted`}>
                {row.unitPrice != null
                  ? t("inventoryPage.trxLastPrice", { price: formatUnitPrice(row.unitPrice) })
                  : t("inventoryPage.trxNoPrice")}
              </span>
            </div>

            <Cell>
              <span className="text-[12px] leading-[normal] text-muted">{row.unit ?? "—"}</span>
            </Cell>

            <Cell right>
              <span className={`${NUMBER_CLASS} ${row.overStock ? "font-bold text-danger-ink" : "text-body"}`}>
                {row.available == null ? "…" : `${formatQty(row.available, row.decimals)}${unitSuffix}`}
              </span>
            </Cell>

            <div className="border-r border-shade-2 p-[2px]">
              <input
                id={cellId(row.productId, 0)}
                data-rk={row.productId}
                data-c={0}
                inputMode="decimal"
                value={row.draft.quantity}
                onChange={(event) => onQuantityChange(row.productId, event.target.value)}
                onBlur={() => onQuantityBlur(row.productId)}
                onFocus={(event) => event.currentTarget.select()}
                placeholder={row.decimals > 0 ? `0,${"0".repeat(row.decimals)}` : "0"}
                className={`${FLAT_CELL_CLASS} ${SHEET_INPUT} px-[9px] ${
                  row.overStock ? FLAT_CELL_ERROR_CLASS : ""
                }`}
              />
            </div>

            {/* Партида: общият PickCell — Select се монтира при клик/Enter, а
                след избора фокусът се връща в клетката за стрелките */}
            <div className="flex min-w-0 items-center border-r border-shade-2 p-[2px]">
              <PickCell
                id={cellId(row.productId, 1)}
                rowKey={row.productId}
                col={1}
                className={`${SHEET_INPUT} flex w-full min-w-0 cursor-pointer items-center rounded-[7px] border border-solid border-transparent bg-transparent px-[9px] text-left text-[12.5px] leading-[normal] text-body outline-none hover:border-shade-3 hover:bg-surface focus:border-brand focus:bg-surface`}
                onSideExit={(step) => onSideExit(row.productId, 1, step)}
                editorHeight={SHEET_CONTROL_HEIGHT}
                renderEditor={(close) => (
                  <Select
                    autoFocus
                    defaultOpen
                    value={row.draft.lotId ? String(row.draft.lotId) : "fifo"}
                    onChange={(value) => {
                      onLotChange(row.productId, value === "fifo" ? null : Number(value));
                      close();
                    }}
                    onBlur={() => close(false)}
                    popupMatchSelectWidth={false}
                    className="w-full"
                    options={[
                      { value: "fifo", label: t("inventoryPage.fifo") },
                      ...row.lots.map((lot) => ({
                        value: String(lot.id),
                        label: lotLabel(lot, row.decimals, row.unit, t),
                      })),
                    ]}
                  />
                )}
              >
                <span className="truncate">
                  {row.draft.lotId
                    ? (row.lots
                        .filter((lot) => lot.id === row.draft.lotId)
                        .map((lot) => lotLabel(lot, row.decimals, row.unit, t))[0] ??
                      t("inventoryPage.fifo"))
                    : t("inventoryPage.fifo")}
                </span>
              </PickCell>
            </div>

            <Cell right>
              <span className={`${NUMBER_CLASS} font-semibold text-body`}>
                {row.remainingInSource == null
                  ? "…"
                  : `${formatQty(Math.max(0, row.remainingInSource), row.decimals)}${unitSuffix}`}
              </span>
            </Cell>

            <Cell right>
              <span className={`${NUMBER_CLASS} font-semibold text-ok-deep`}>
                {row.resultingInTarget == null
                  ? "…"
                  : `${formatQty(row.resultingInTarget, row.decimals)}${unitSuffix}`}
              </span>
            </Cell>

            <Cell right>
              <span className={`${NUMBER_CLASS} text-body`}>
                {row.unitPrice != null ? formatUnitPrice(row.unitPrice) : "—"}
              </span>
            </Cell>

            <Cell right>
              <span className={`${NUMBER_CLASS} text-body`}>{row.net > 0 ? formatMoney(row.net) : "—"}</span>
            </Cell>

            <Cell right>
              <span className={`${NUMBER_CLASS} font-bold text-ink`}>
                {row.gross > 0 ? formatMoney(row.gross) : "—"}
              </span>
            </Cell>

            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={() => onRemove(row.productId)}
                aria-label={t("inventoryPage.wsClearRowHint")}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[13px] text-ghost hover:bg-shade-1 hover:text-danger-ink"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
        );
      })}
      <TransferTotalsRow count={rows.length} totalNet={totalNet} totalGross={totalGross} />
    </div>
  );
};

// Празният лист: какво да се направи и клавишите (на десктоп)
const TransferEmptyState = ({ isDesktop }: { isDesktop: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2.5 px-6 py-16 text-center">
      <div className="flex h-[46px] w-[46px] items-center justify-center rounded-[13px] bg-brand-tint">
        <SearchOutlined className="text-xl text-brand-deep-ink" />
      </div>
      <div className="text-[15px] font-bold leading-[normal] text-ink">{t("inventoryPage.wsEmptyTitle")}</div>
      <div className="max-w-[480px] text-[12.5px] leading-[1.6] text-muted">{t("inventoryPage.wsEmptyHint")}</div>
      {isDesktop && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-1.5 text-xs leading-[normal] text-muted">
          <KbdChip>А–Я</KbdChip> {t("inventoryPage.wsEmptyKbdType")}
          <span className="text-ghost">·</span>
          <KbdChip>↵</KbdChip> {t("inventoryPage.wsEmptyKbdEnter")}
          <span className="text-ghost">·</span>
          <KbdChip>Esc</KbdChip> {t("inventoryPage.wsEmptyKbdEsc")}
        </div>
      )}
    </div>
  );
};

// ─── Помощни клетъчни компоненти ────────────────────────────────────────────
function Th({ children, center, right }: { children?: React.ReactNode; center?: boolean; right?: boolean }) {
  return (
    <div
      className={`${SHEET_HEADER_CELL} border-r border-shade-2 ${
        center ? "!px-2 justify-center text-center" : right ? "justify-end text-right" : "justify-start text-left"
      }`}
    >
      <span className="truncate">{children}</span>
    </div>
  );
}

// Статична клетка (число/текст) с колонния бордюр на решетката
function Cell({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <div className={`flex min-w-0 items-center border-r border-shade-2 px-2.5 ${right ? "justify-end" : ""}`}>
      {children}
    </div>
  );
}

export default TransferWorksheetTable;
